import React from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Alert,
  Tooltip,
  Stack,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import PersonOffRounded from '@mui/icons-material/PersonOffRounded';
import PersonRounded from '@mui/icons-material/PersonRounded';
import LockResetRounded from '@mui/icons-material/LockResetRounded';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AdminPanelSettingsRounded from '@mui/icons-material/AdminPanelSettingsRounded';
import ShieldRounded from '@mui/icons-material/ShieldRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import { AppDialog, ConfirmDialog } from '../components/dialog';
import { SnackbarContext } from '../ui/SnackbarProvider';
import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';
import { formatRelativeTime, formatAbsolute } from '../utils/date';

interface UserItem {
  user_id: string;
  name: string;
  email: string;
  is_root: boolean;
  role_id: string | null;
  role_name: string | null;
  workflow_persona: string;
  deactivated_at: string | null;
  last_login_at: string | null;
  has_password: boolean;
  invited_by_user_id: string | null;
  last_invite_sent_at: string | null;
  invite_accepted_at: string | null;
  invite_pending: boolean;
  created_at: string;
  updated_at: string;
}

interface RoleItem {
  role_id: string;
  name: string;
  description: string | null;
  is_system: boolean;
  // Present on the roles endpoint; lets the picker say what each role can
  // reach, so choosing one is not a guess about its data access.
  editable?: boolean;
  user_count?: number;
  denied_table_count?: number;
  denied_column_count?: number;
}

/**
 * Last-login copy: relative ("2 hours ago") for the table cell, with the
 * absolute timestamp surfaced in a tooltip via `formatAbsolute`. Returns
 * "Never" when the user hasn't logged in yet so the cell never reads as
 * a stale "—". Centralised helpers live in `utils/date.ts` — keep this
 * function only as a thin "Never" guard around them.
 */
function formatLastLoginRelative(iso: string | null): string {
  if (!iso) return 'Never';
  return formatRelativeTime(iso) || 'Never';
}

/** Title-case role for display (mobile cards use text-only chips for consistent height). */
function formatRoleLabel(roleName: string | null) {
  const raw = (roleName || 'Admin').trim();
  if (!raw) return 'Admin';
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

/** Shared dimensions so role / persona / status chips align on mobile. */
const mobileUserMetaChipSx = {
  height: 26,
  maxHeight: 26,
  boxSizing: 'border-box' as const,
  fontWeight: 500,
  '& .MuiChip-label': {
    px: 1,
    py: 0,
    fontSize: '0.75rem',
    lineHeight: 1.25,
  },
};

export default function UsersPage() {
  const { notify } = React.useContext(SnackbarContext);
  const [users, setUsers] = React.useState<UserItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Roles
  const [roles, setRoles] = React.useState<RoleItem[]>([]);

  // Invite user dialog (passwordless — no password field)
  const [createOpen, setCreateOpen] = React.useState(false);
  const [createForm, setCreateForm] = React.useState({
    name: '',
    email: '',
    role_id: '',
    workflow_persona: 'technical',
    message: '',
  });
  const [createError, setCreateError] = React.useState<string | null>(null);
  const [createLoading, setCreateLoading] = React.useState(false);

  // Edit user dialog
  const [editOpen, setEditOpen] = React.useState(false);
  const [editUser, setEditUser] = React.useState<UserItem | null>(null);
  const [editForm, setEditForm] = React.useState({ name: '', workflow_persona: 'technical', role_id: '' });
  const [editError, setEditError] = React.useState<string | null>(null);
  const [editLoading, setEditLoading] = React.useState(false);

  // Reset password dialog
  const [resetOpen, setResetOpen] = React.useState(false);
  const [resetUserId, setResetUserId] = React.useState<string | null>(null);
  const [resetUserName, setResetUserName] = React.useState('');
  const [resetPassword, setResetPassword] = React.useState('');
  const [resetError, setResetError] = React.useState<string | null>(null);
  const [resetLoading, setResetLoading] = React.useState(false);
  const [showResetPassword, setShowResetPassword] = React.useState(false);

  // Deactivate confirm dialog
  const [deactivateOpen, setDeactivateOpen] = React.useState(false);
  const [deactivateUser, setDeactivateUser] = React.useState<UserItem | null>(null);
  const [deactivateLoading, setDeactivateLoading] = React.useState(false);

  // Resend-invite tracks per-user pending state so the row spinner
  // can replace the icon while the request is in flight.
  const [resendingUserId, setResendingUserId] = React.useState<string | null>(null);

  const currentUserId = React.useMemo(() => {
    try {
      const u = JSON.parse(String(window.localStorage.getItem('klaris_user') || '{}'));
      return u?.user_id || '';
    } catch { return ''; }
  }, []);

  const token = React.useMemo(() => window.localStorage.getItem('klaris_jwt') || '', []);

  const fetchUsers = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const tid = getTenantIdFromStorage();
      if (!tid) throw new Error('Not authenticated');
      const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tid)}/users`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data?.detail || 'Failed to load users');
      }
      const data = await resp.json();
      setUsers(data.users || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchRoles = React.useCallback(async () => {
    try {
      const tid = getTenantIdFromStorage();
      if (!tid) return;
      const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tid)}/roles`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        // The roles endpoint returns a bare array. Older builds wrapped it in
        // { roles: [...] }, so accept both while clients update.
        setRoles(Array.isArray(data) ? data : (data.roles || []));
      }
    } catch {
      // roles are non-critical; admin is the default
    }
  }, [token]);

  React.useEffect(() => { fetchUsers(); fetchRoles(); }, [fetchUsers, fetchRoles]);

  const handleInvite = async () => {
    setCreateError(null);
    if (!createForm.name.trim() || !createForm.email.trim()) {
      setCreateError('Name and email are required');
      return;
    }
    setCreateLoading(true);
    try {
      const tid = getTenantIdFromStorage();
      if (!tid) throw new Error('Not authenticated');
      const payload: any = {
        name: createForm.name,
        email: createForm.email,
        workflow_persona: createForm.workflow_persona,
      };
      if (createForm.role_id) payload.role_id = createForm.role_id;
      if (createForm.message.trim()) payload.message = createForm.message.trim();
      const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tid)}/users`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || 'Failed to invite user');
      notify(`Invite sent to ${createForm.email}`, 'success');
      setCreateOpen(false);
      setCreateForm({ name: '', email: '', role_id: '', workflow_persona: 'technical', message: '' });
      fetchUsers();
    } catch (e: any) {
      setCreateError(e?.message || 'Failed to invite user');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!editUser) return;
    setEditError(null);
    if (!editForm.name.trim()) { setEditError('Name is required'); return; }
    setEditLoading(true);
    try {
      const tid = getTenantIdFromStorage();
      if (!tid) throw new Error('Not authenticated');
      const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tid)}/users/${editUser.user_id}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: editForm.name,
          workflow_persona: editForm.workflow_persona,
          // Omitted when unchanged, and always for yourself — the server
          // refuses a self-change, and sending it would fail the whole save.
          ...(editForm.role_id && editForm.role_id !== editUser?.role_id
              && editUser?.user_id !== currentUserId
                ? { role_id: editForm.role_id } : {}),
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || 'Failed to update user');
      notify('User updated successfully', 'success');
      setEditOpen(false);
      setEditUser(null);
      fetchUsers();
    } catch (e: any) {
      setEditError(e?.message || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateUser) return;
    setDeactivateLoading(true);
    try {
      const tid = getTenantIdFromStorage();
      if (!tid) throw new Error('Not authenticated');
      const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tid)}/users/${deactivateUser.user_id}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || 'Failed to deactivate user');
      notify('User deactivated', 'success');
      setDeactivateOpen(false);
      setDeactivateUser(null);
      fetchUsers();
    } catch (e: any) {
      notify(e?.message || 'Failed to deactivate user', 'error');
    } finally {
      setDeactivateLoading(false);
    }
  };

  const handleReactivate = async (userId: string) => {
    try {
      const tid = getTenantIdFromStorage();
      if (!tid) throw new Error('Not authenticated');
      const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tid)}/users/${userId}/reactivate`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || 'Failed to reactivate user');
      notify('User reactivated', 'success');
      fetchUsers();
    } catch (e: any) {
      notify(e?.message || 'Failed to reactivate user', 'error');
    }
  };

  const handleResendInvite = async (user: UserItem) => {
    setResendingUserId(user.user_id);
    try {
      const tid = getTenantIdFromStorage();
      if (!tid) throw new Error('Not authenticated');
      const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tid)}/users/${user.user_id}/resend-invite`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.detail || 'Failed to resend invite');
      notify(`Invite resent to ${user.email}`, 'success');
      fetchUsers();
    } catch (e: any) {
      notify(e?.message || 'Failed to resend invite', 'error');
    } finally {
      setResendingUserId(null);
    }
  };

  const handleResetPassword = async () => {
    setResetError(null);
    if (!resetPassword) {
      setResetError('Password is required');
      return;
    }
    setResetLoading(true);
    try {
      const tid = getTenantIdFromStorage();
      if (!tid) throw new Error('Not authenticated');
      const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tid)}/users/${resetUserId}/reset-password`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ new_password: resetPassword }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || 'Failed to reset password');
      notify('Password reset successfully', 'success');
      setResetOpen(false);
      setResetPassword('');
      setResetUserId(null);
    } catch (e: any) {
      setResetError(e?.message || 'Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  const activeUsers = users.filter(u => !u.deactivated_at);
  const deactivatedUsers = users.filter(u => u.deactivated_at);

  // ── Search + pagination ────────────────────────────────────────────────────
  // Lightweight client-side filtering. The /users endpoint already returns
  // the whole tenant in one shot, so paginating server-side would just add
  // round trips for no win at our current per-tenant scale. If we ever
  // exceed a few hundred users per tenant we'll switch this to server-side.
  const [query, setQuery] = React.useState('');
  const [activePage, setActivePage] = React.useState(0);
  const [activeRowsPerPage, setActiveRowsPerPage] = React.useState(10);
  const [deactivatedPage, setDeactivatedPage] = React.useState(0);
  const [deactivatedRowsPerPage, setDeactivatedRowsPerPage] = React.useState(10);

  const matchesQuery = React.useCallback(
    (u: UserItem) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return (
        u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
      );
    },
    [query],
  );

  const filteredActive = React.useMemo(
    () => activeUsers.filter(matchesQuery),
    [activeUsers, matchesQuery],
  );
  const filteredDeactivated = React.useMemo(
    () => deactivatedUsers.filter(matchesQuery),
    [deactivatedUsers, matchesQuery],
  );

  // Snap pages back into range when the underlying list shrinks (search,
  // deactivation, etc.) so we don't render an empty table.
  React.useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredActive.length / activeRowsPerPage) - 1,
    );
    if (activePage > maxPage) setActivePage(maxPage);
  }, [filteredActive.length, activeRowsPerPage, activePage]);

  React.useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredDeactivated.length / deactivatedRowsPerPage) - 1,
    );
    if (deactivatedPage > maxPage) setDeactivatedPage(maxPage);
  }, [filteredDeactivated.length, deactivatedRowsPerPage, deactivatedPage]);

  const pagedActive = React.useMemo(() => {
    const start = activePage * activeRowsPerPage;
    return filteredActive.slice(start, start + activeRowsPerPage);
  }, [filteredActive, activePage, activeRowsPerPage]);

  const pagedDeactivated = React.useMemo(() => {
    const start = deactivatedPage * deactivatedRowsPerPage;
    return filteredDeactivated.slice(start, start + deactivatedRowsPerPage);
  }, [filteredDeactivated, deactivatedPage, deactivatedRowsPerPage]);

  const openCreate = () => {
    setCreateForm({
      name: '',
      email: '',
      role_id: roles.length > 0 ? roles[0].role_id : '',
      workflow_persona: 'technical',
      message: '',
    });
    setCreateError(null);
    setCreateOpen(true);
  };

  const addUserBtnSx = {
    borderRadius: '7px',
    textTransform: 'none',
    height: { xs: 40, md: 32 },
    fontSize: '0.8125rem',
    fontWeight: 600,
    boxShadow: 'none',
    '&:hover': { boxShadow: 'none' },
    width: { xs: '100%', md: 'auto' },
  } as const;

  /**
   * Status pill copy/colour. Pending for invited-but-never-logged-in users
   * is the most useful signal an admin gets — they need to know whether
   * to nudge the invitee or whether the user is fully onboarded.
   */
  const renderStatusChip = (user: UserItem) => {
    if (user.invite_pending) {
      return (
        <Chip
          label="Pending invite"
          size="small"
          color="warning"
          variant="outlined"
          sx={{ fontWeight: 500 }}
        />
      );
    }
    return <Chip label="Active" size="small" color="success" variant="outlined" sx={{ fontWeight: 500 }} />;
  };

  return (
    // Layout-level spacer (DashboardLayout) handles bottom breathing room
    // for every routed page now, so no `pb` here.
    <Box sx={{ width: '100%', minWidth: 0 }}>
      {/* Page header — no in-page H5 title; the breadcrumb above already
          identifies the page. Member count chip + subtitle on the left,
          primary CTA on the right. Same shape as Connectors / Knowledge
          Base / Data Model so the product reads as one app. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1.5, sm: 2 },
          mb: 2.5,
          pb: 1.5,
          borderBottom: (t: any) => `1px solid ${t.tokens.border.subtle}`,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {/* No metadata chip — the "Active members · N" sunken header strip
              on the Paper below already surfaces the count, so a duplicate
              chip up here was just noise. Subtitle alone matches Connectors
              / Knowledge Base / Settings sub-pages. */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: '0.8125rem', lineHeight: 1.45 }}
          >
            Manage members of your organization, their roles, and personas.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddRounded />}
          size="small"
          onClick={openCreate}
          sx={{ ...addUserBtnSx, alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
        >
          Invite user
        </Button>
      </Box>

      {/* Active users — Paper + sunken header strip, same shell as Data Model /
          Knowledge Base / Connectors so the whole product reads consistently. */}
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          borderColor: (t: any) => t.tokens.border.subtle,
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: (t: any) => `1px solid ${t.tokens.border.subtle}`,
            backgroundColor: (t: any) =>
              t.tokens.surface?.sunken || t.palette.action.hover,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              fontSize: '0.6875rem',
              color: 'text.secondary',
            }}
          >
            Active members
            {!loading && !error
              ? query.trim()
                ? ` · ${filteredActive.length} of ${activeUsers.length}`
                : ` · ${activeUsers.length}`
              : ''}
          </Typography>
          {/* Search is intentionally inline with the section header so
              it doesn't add another row of UI chrome. The disabled state
              while loading prevents typing into a list that hasn't
              materialized yet. */}
          <TextField
            size="small"
            placeholder="Search by name or email…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActivePage(0);
              setDeactivatedPage(0);
            }}
            disabled={loading || !!error}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded sx={{ fontSize: 16, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '60%', sm: 280 },
              '& .MuiInputBase-root': {
                fontSize: '0.8125rem',
                bgcolor: 'background.paper',
              },
            }}
          />
        </Box>
        <Box sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ m: { xs: 2, sm: 2 } }}>{error}</Alert>
          ) : (
            <>
              <TableContainer sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto' }}>
                <Table size="medium">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Persona</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Last Login</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedActive.map((user) => (
                      <TableRow key={user.user_id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                              {user.name.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {user.name}
                                {user.is_root && (
                                  <Tooltip title="Root user (cannot be removed)">
                                    <ShieldRounded sx={{ fontSize: 14, ml: 0.5, color: 'warning.main', verticalAlign: 'middle' }} />
                                  </Tooltip>
                                )}
                              </Typography>
                              {user.user_id === currentUserId && (
                                <Typography variant="caption" color="text.secondary">(You)</Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{user.email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={<AdminPanelSettingsRounded sx={{ fontSize: 16 }} />}
                            label={formatRoleLabel(user.role_name)}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ textTransform: 'none' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.workflow_persona === 'business' ? 'Business' : 'Technical'}
                            size="small"
                            variant="outlined"
                            color={user.workflow_persona === 'business' ? 'secondary' : 'default'}
                            sx={{ fontWeight: 500, textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>{renderStatusChip(user)}</TableCell>
                        <TableCell>
                          {user.last_login_at ? (
                            <Tooltip title={formatAbsolute(user.last_login_at)}>
                              <Typography variant="body2" color="text.secondary">
                                {formatLastLoginRelative(user.last_login_at)}
                              </Typography>
                            </Tooltip>
                          ) : user.invite_pending && user.last_invite_sent_at ? (
                            <Tooltip title={`Invite sent ${formatAbsolute(user.last_invite_sent_at)}`}>
                              <Typography variant="body2" color="text.secondary">
                                Invited {formatRelativeTime(user.last_invite_sent_at)}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Never
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {user.invite_pending && (
                              <Tooltip title="Resend invite email">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleResendInvite(user)}
                                    disabled={resendingUserId === user.user_id}
                                  >
                                    {resendingUserId === user.user_id ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <MailOutlineRounded fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip title="Edit user">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditUser(user);
                                  setEditForm({ name: user.name, workflow_persona: user.workflow_persona || 'technical', role_id: user.role_id || '' });
                                  setEditError(null);
                                  setEditOpen(true);
                                }}
                              >
                                <EditRounded fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {/* Reset-password is only meaningful for users
                                who actually have a password. Hide it for
                                invitees and OTP-only users so the row is
                                less cluttered. */}
                            {user.has_password && (
                              <Tooltip title="Reset password">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setResetUserId(user.user_id);
                                    setResetUserName(user.name);
                                    setResetPassword('');
                                    setResetError(null);
                                    setResetOpen(true);
                                  }}
                                >
                                  <LockResetRounded fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {!user.is_root && user.user_id !== currentUserId && (
                              <Tooltip title="Deactivate user">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => { setDeactivateUser(user); setDeactivateOpen(true); }}
                                >
                                  <PersonOffRounded fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredActive.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            {query.trim()
                              ? `No users match "${query.trim()}"`
                              : 'No active users found'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Stack
                spacing={2}
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  p: { xs: 2, sm: 2.5 },
                  pt: { xs: 2, sm: 2.5 },
                }}
              >
                {pagedActive.map((user) => (
                  <Paper
                    key={user.user_id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      minWidth: 0,
                    }}
                  >
                    <Stack spacing={0}>
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                          <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontSize: '0.9rem', flexShrink: 0 }}>
                            {user.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                              {user.name}
                              {user.is_root && (
                                <Tooltip title="Root user (cannot be removed)">
                                  <ShieldRounded sx={{ fontSize: 14, ml: 0.5, color: 'warning.main', verticalAlign: 'middle' }} />
                                </Tooltip>
                              )}
                              {user.user_id === currentUserId && (
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                  (You)
                                </Typography>
                              )}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all', fontSize: '0.8125rem', mt: 0.25 }}>
                              {user.email}
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={0.25} flexWrap="wrap" justifyContent="flex-end" sx={{ flexShrink: 0 }}>
                          {user.invite_pending && (
                            <Tooltip title="Resend invite email">
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleResendInvite(user)}
                                  disabled={resendingUserId === user.user_id}
                                >
                                  {resendingUserId === user.user_id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <MailOutlineRounded fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title="Edit user">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditUser(user);
                                setEditForm({ name: user.name, workflow_persona: user.workflow_persona || 'technical', role_id: user.role_id || '' });
                                setEditError(null);
                                setEditOpen(true);
                              }}
                            >
                              <EditRounded fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {user.has_password && (
                            <Tooltip title="Reset password">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setResetUserId(user.user_id);
                                  setResetUserName(user.name);
                                  setResetPassword('');
                                  setResetError(null);
                                  setResetOpen(true);
                                }}
                              >
                                <LockResetRounded fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {!user.is_root && user.user_id !== currentUserId && (
                            <Tooltip title="Deactivate user">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => { setDeactivateUser(user); setDeactivateOpen(true); }}
                              >
                                <PersonOffRounded fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </Stack>

                      <Divider sx={{ my: 1.5, borderColor: 'divider' }} />

                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.6875rem', display: 'block', mb: 0.75 }}>
                        Role · Persona · Status
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: 1,
                          rowGap: 1,
                        }}
                      >
                        <Chip
                          label={formatRoleLabel(user.role_name)}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ ...mobileUserMetaChipSx, textTransform: 'none' }}
                        />
                        <Chip
                          label={user.workflow_persona === 'business' ? 'Business' : 'Technical'}
                          size="small"
                          variant="outlined"
                          color={user.workflow_persona === 'business' ? 'secondary' : 'default'}
                          sx={{ ...mobileUserMetaChipSx, textTransform: 'capitalize' }}
                        />
                        <Chip
                          label={user.invite_pending ? 'Pending invite' : 'Active'}
                          size="small"
                          color={user.invite_pending ? 'warning' : 'success'}
                          variant="outlined"
                          sx={mobileUserMetaChipSx}
                        />
                      </Box>

                      {user.last_login_at ? (
                        <Tooltip title={formatAbsolute(user.last_login_at)}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.75rem', lineHeight: 1.5, mt: 1.25, alignSelf: 'flex-start' }}
                          >
                            Last login: {formatLastLoginRelative(user.last_login_at)}
                          </Typography>
                        </Tooltip>
                      ) : user.invite_pending && user.last_invite_sent_at ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.75rem', lineHeight: 1.5, mt: 1.25 }}
                        >
                          Invited {formatRelativeTime(user.last_invite_sent_at)}
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.75rem', lineHeight: 1.5, mt: 1.25 }}
                        >
                          Last login: Never
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                ))}
                {filteredActive.length === 0 && (
                  <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                    {query.trim()
                      ? `No users match "${query.trim()}"`
                      : 'No active users found'}
                  </Typography>
                )}
              </Stack>

              {filteredActive.length > 0 && (
                <TablePagination
                  component="div"
                  count={filteredActive.length}
                  page={activePage}
                  onPageChange={(_, p) => setActivePage(p)}
                  rowsPerPage={activeRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setActiveRowsPerPage(parseInt(e.target.value, 10));
                    setActivePage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50]}
                  sx={{
                    borderTop: (t: any) => `1px solid ${t.tokens.border.subtle}`,
                    '& .MuiTablePagination-toolbar': { minHeight: 48 },
                  }}
                />
              )}
            </>
          )}
        </Box>
      </Paper>

      {/* Deactivated members — same Paper + sunken header strip shell so the
          two sections feel like siblings, not stylistically different. */}
      {deactivatedUsers.length > 0 && (
        <Paper
          variant="outlined"
          sx={{
            mt: 3,
            borderRadius: 2,
            overflow: 'hidden',
            borderColor: (t: any) => t.tokens.border.subtle,
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.25,
              borderBottom: (t: any) => `1px solid ${t.tokens.border.subtle}`,
              backgroundColor: (t: any) =>
                t.tokens.surface?.sunken || t.palette.action.hover,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                fontSize: '0.6875rem',
                color: 'text.secondary',
              }}
            >
              Deactivated
              {query.trim()
                ? ` · ${filteredDeactivated.length} of ${deactivatedUsers.length}`
                : ` · ${deactivatedUsers.length}`}
            </Typography>
          </Box>
          <Box sx={{ p: 0 }}>
            <TableContainer sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto' }}>
              <Table size="medium">
                <TableBody>
                  {pagedDeactivated.map((user) => (
                    <TableRow key={user.user_id} sx={{ opacity: 0.7 }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.400', fontSize: '0.875rem' }}>
                            {user.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="body2" fontWeight={600}>{user.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{user.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label="Deactivated" size="small" color="default" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {user.deactivated_at ? (
                          <Tooltip title={formatAbsolute(user.deactivated_at)}>
                            <Typography variant="body2" color="text.secondary">
                              Deactivated {formatRelativeTime(user.deactivated_at)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Deactivated
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Reactivate user">
                          <IconButton size="small" color="success" onClick={() => handleReactivate(user.user_id)}>
                            <PersonRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredDeactivated.length === 0 && query.trim() && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">
                          No deactivated users match "{query.trim()}"
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Stack
              spacing={2}
              sx={{
                display: { xs: 'flex', md: 'none' },
                p: { xs: 2, sm: 2.5 },
              }}
            >
              {pagedDeactivated.map((user) => (
                <Paper
                  key={user.user_id}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    opacity: 0.92,
                    minWidth: 0,
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                      <Avatar sx={{ width: 40, height: 40, bgcolor: 'grey.400', fontSize: '0.9rem', flexShrink: 0 }}>
                        {user.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {user.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all', fontSize: '0.8125rem' }}>
                          {user.email}
                        </Typography>
                        {user.deactivated_at ? (
                          <Tooltip title={formatAbsolute(user.deactivated_at)}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              Deactivated {formatRelativeTime(user.deactivated_at)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            Deactivated
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    <Tooltip title="Reactivate user">
                      <IconButton size="small" color="success" onClick={() => handleReactivate(user.user_id)} sx={{ flexShrink: 0 }}>
                        <PersonRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Box sx={{ mt: 1.25 }}>
                    <Chip label="Deactivated" size="small" color="default" variant="outlined" sx={mobileUserMetaChipSx} />
                  </Box>
                </Paper>
              ))}
              {filteredDeactivated.length === 0 && query.trim() && (
                <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                  No deactivated users match "{query.trim()}"
                </Typography>
              )}
            </Stack>

            {filteredDeactivated.length > 0 && (
              <TablePagination
                component="div"
                count={filteredDeactivated.length}
                page={deactivatedPage}
                onPageChange={(_, p) => setDeactivatedPage(p)}
                rowsPerPage={deactivatedRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setDeactivatedRowsPerPage(parseInt(e.target.value, 10));
                  setDeactivatedPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                sx={{
                  borderTop: (t: any) => `1px solid ${t.tokens.border.subtle}`,
                  '& .MuiTablePagination-toolbar': { minHeight: 48 },
                }}
              />
            )}
          </Box>
        </Paper>
      )}

      <AppDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        size="xs"
        title="Invite user"
        subtitle="They'll receive an email with a one-click sign-in link."
        busy={createLoading}
        primaryAction={{ label: 'Send invite', onClick: handleInvite }}
        secondaryAction={{ label: 'Cancel', onClick: () => setCreateOpen(false), color: 'inherit' }}
      >
        {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            autoFocus
            fullWidth
            label="Full Name"
            value={createForm.name}
            onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={createForm.email}
            onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
          />
          <FormControl fullWidth>
            <InputLabel id="create-role-label">Role</InputLabel>
            <Select
              labelId="create-role-label"
              value={createForm.role_id}
              label="Role"
              onChange={(e) => setCreateForm({ ...createForm, role_id: e.target.value as string })}
            >
              {roles.map((r) => (
                <MenuItem key={r.role_id} value={r.role_id}>
                  {r.name.charAt(0).toUpperCase() + r.name.slice(1)}{r.description ? ` - ${r.description}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="create-persona-label">Persona</InputLabel>
            <Select
              labelId="create-persona-label"
              value={createForm.workflow_persona}
              label="Persona"
              onChange={(e) => setCreateForm({ ...createForm, workflow_persona: e.target.value as string })}
            >
              <MenuItem value="technical">Technical - detailed insights and data-centric views</MenuItem>
              <MenuItem value="business">Business - simplified, business-friendly language</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Personal note (optional)"
            value={createForm.message}
            onChange={(e) => setCreateForm({ ...createForm, message: e.target.value })}
            placeholder="Hey, I'd like you to join our Klaris workspace..."
            multiline
            minRows={2}
            maxRows={4}
            inputProps={{ maxLength: 500 }}
            helperText="Included in the invite email. Leave blank to use the default greeting."
          />
        </Stack>
      </AppDialog>

      <AppDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        size="xs"
        title="Edit user"
        busy={editLoading}
        primaryAction={{ label: 'Save changes', onClick: handleEdit }}
        secondaryAction={{ label: 'Cancel', onClick: () => setEditOpen(false), color: 'inherit' }}
      >
        {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            autoFocus
            fullWidth
            label="Full Name"
            value={editForm.name}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
          />
          <FormControl fullWidth>
            <InputLabel id="edit-persona-label">Persona</InputLabel>
            <Select
              labelId="edit-persona-label"
              value={editForm.workflow_persona}
              label="Persona"
              onChange={(e) => setEditForm({ ...editForm, workflow_persona: e.target.value as string })}
            >
              <MenuItem value="technical">Technical - detailed insights and data-centric views</MenuItem>
              <MenuItem value="business">Business - simplified, business-friendly language</MenuItem>
            </Select>
          </FormControl>
          {/* Role sits with name and persona rather than being editable from
              the row, so everything about a member is changed in one place.
              What a role can reach is shown inline, because picking one is a
              decision about data access, not just a label. */}
          <FormControl fullWidth size="small" disabled={editUser?.user_id === currentUserId}>
            <InputLabel id="edit-role-label">Role</InputLabel>
            <Select
              labelId="edit-role-label"
              label="Role"
              value={roles.some((r) => r.role_id === editForm.role_id) ? editForm.role_id : ''}
              onChange={(e) => setEditForm({ ...editForm, role_id: String(e.target.value) })}
              renderValue={(value) => {
                const picked = roles.find((r) => r.role_id === value);
                return formatRoleLabel(picked?.name ?? editUser?.role_name ?? null);
              }}
            >
              {roles.map((r) => (
                <MenuItem key={r.role_id} value={r.role_id}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                      {r.name}
                    </Typography>
                    {typeof r.denied_table_count === 'number' && (
                      <Typography variant="caption" color="text.secondary">
                        {/* What is hidden is set and listed in the Data model;
                            here it is enough to know whether anything is. */}
                        {r.denied_table_count + (r.denied_column_count ?? 0) > 0
                          ? 'Some data is hidden from this role'
                          : 'Sees all data'}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {editUser?.user_id === currentUserId && (
            <Alert severity="info" icon={false}>
              Changing your own persona requires a re-login to take effect.
              You cannot change your own role — ask another admin.
            </Alert>
          )}
        </Stack>
      </AppDialog>

      <AppDialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        size="xs"
        title="Reset password"
        subtitle={resetUserName ? `Set a new password for ${resetUserName}.` : undefined}
        busy={resetLoading}
        primaryAction={{ label: 'Reset password', onClick: handleResetPassword }}
        secondaryAction={{ label: 'Cancel', onClick: () => setResetOpen(false), color: 'inherit' }}
      >
        {resetError && <Alert severity="error" sx={{ mb: 2 }}>{resetError}</Alert>}
        <TextField
          autoFocus
          fullWidth
          label="New Password"
          type={showResetPassword ? 'text' : 'password'}
          value={resetPassword}
          onChange={(e) => setResetPassword(e.target.value)}
          helperText="Min 8 chars, 1 letter, 1 number, 1 symbol"
          sx={{ mt: 0.5 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowResetPassword(!showResetPassword)} edge="end" size="small">
                  {showResetPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </AppDialog>

      <ConfirmDialog
        open={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
        onConfirm={handleDeactivate}
        tone="warning"
        title="Deactivate user?"
        confirmLabel="Deactivate"
        busy={deactivateLoading}
        description={
          <>
            Are you sure you want to deactivate <strong>{deactivateUser?.name}</strong> (
            {deactivateUser?.email})? They will be signed out immediately and cannot log in
            again. Their data (dashboards, saved queries, chat threads) is preserved and you
            can reactivate them later.
          </>
        }
      />
    </Box>
  );
}
